import { inject, type InjectionKey, provide, type Ref, ref } from "vue";
import type { LocationQueryRaw } from "vue-router";

export interface SubmenuOverrideItem {
  name: string;
  href: string;
  query?: LocationQueryRaw;
}

export interface SubmenuOverrideBackLink {
  to: string | { name: string; query?: Record<string, string> };
  label: string;
}

export interface SubmenuOverride {
  title: string;
  items: SubmenuOverrideItem[];
  parentMenu: string;
  backLink?: SubmenuOverrideBackLink;
}

interface SubmenuOverrideContext {
  override: Ref<SubmenuOverride | null>;
  isSuppressed: Ref<boolean>;
  wasSuppressedOnClear: Ref<boolean>;
  setOverride: (override: SubmenuOverride | null) => void;
  clearOverride: () => void;
  suppressOverride: () => void;
  unsuppressOverride: () => void;
}

const SUBMENU_OVERRIDE_KEY: InjectionKey<SubmenuOverrideContext> =
  Symbol("submenuOverride");

export function provideSubmenuOverride(): SubmenuOverrideContext {
  const override = ref<SubmenuOverride | null>(null);
  const isSuppressed = ref(false);
  const wasSuppressedOnClear = ref(false);

  const context: SubmenuOverrideContext = {
    override,
    isSuppressed,
    wasSuppressedOnClear,
    setOverride: (newOverride) => {
      override.value = newOverride;
      if (newOverride) {
        isSuppressed.value = false;
        wasSuppressedOnClear.value = false;
      }
    },
    clearOverride: () => {
      wasSuppressedOnClear.value = isSuppressed.value;
      override.value = null;
      isSuppressed.value = false;
    },
    suppressOverride: () => {
      isSuppressed.value = true;
    },
    unsuppressOverride: () => {
      isSuppressed.value = false;
    },
  };

  provide(SUBMENU_OVERRIDE_KEY, context);

  return context;
}
export function useSubmenuOverride(): SubmenuOverrideContext {
  const context = inject(SUBMENU_OVERRIDE_KEY);

  if (!context) {
    console.warn(
      "useSubmenuOverride() was called without a provider. " +
        "Make sure provideSubmenuOverride() is called in a parent component.",
    );
    return {
      override: ref(null),
      isSuppressed: ref(false),
      wasSuppressedOnClear: ref(false),
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      setOverride: () => {},
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      clearOverride: () => {},
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      suppressOverride: () => {},
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      unsuppressOverride: () => {},
    };
  }

  return context;
}
